import React, { useState, useEffect, useRef } from 'react';
import { featuredPublications, researchThemes } from '../data/researchData';
import ResearchReel from '../components/ResearchReel';
import '../styles/PageTemplate.css';
import '../styles/ResearchNew.css';

const railLabel = (p) => p.short || (p.title.length > 54 ? p.title.slice(0, 52).trimEnd() + '…' : p.title);

function Figure({ pub }) {
  const [broken, setBroken] = useState(false);
  if (broken) return <div className="rn-fig rn-fig-empty"><span>🧬</span></div>;
  return (
    <div className="rn-fig">
      <img src={pub.image} alt={pub.title} loading="lazy" onError={() => setBroken(true)} />
    </div>
  );
}

function PaperCard({ pub }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="rn-card" id={pub.id} data-spy>
      <Figure pub={pub} />
      <div className="rn-body">
        <p className="rn-cite">
          <span className="rn-journal">{pub.journal}</span>
          <span className="rn-sep">·</span>
          <span>{pub.year}</span>
          <span className="rn-sep">·</span>
          <a className="rn-doi" href={pub.doi} target="_blank" rel="noopener noreferrer">DOI ↗</a>
        </p>
        <h3 className="rn-title">
          <a href={pub.doi} target="_blank" rel="noopener noreferrer">{pub.title}</a>
        </h3>
        <div className={`rn-abstract${open ? ' is-open' : ''}`}>
          {pub.abstract.map((para, i) => <p key={i}>{para}</p>)}
        </div>
      </div>
      {/* 独占一行、横跨整张卡、居中 */}
      <button type="button" className="rn-toggle" onClick={() => setOpen(o => !o)}>
        {open ? 'Show less ▴' : 'Read more ▾'}
      </button>
    </article>
  );
}

function ResearchNew() {
  // 目录只负责"跳"，不负责"显示/隐藏" —— 所有内容始终在页面上，纯滚动也能看完
  const [activeId, setActiveId] = useState(researchThemes[0].key);
  const railRef = useRef(null);

  // IntersectionObserver 比监听 scroll 省事也更准：
  // 取当前所有可见锚点里最靠上的那个作为高亮
  useEffect(() => {
    const targets = document.querySelectorAll('[data-spy]');
    if (!targets.length) return;
    const visible = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) visible.set(e.target.id, e.boundingClientRect.top);
        else visible.delete(e.target.id);
      });
      if (visible.size) {
        setActiveId([...visible.entries()].sort((a, b) => a[1] - b[1])[0][0]);
      }
    }, { rootMargin: '-80px 0px -65% 0px', threshold: 0 });
    targets.forEach(t => io.observe(t));
    return () => io.disconnect();
  }, []);

  // 高亮项滚出侧栏视野时把它带回来
  useEffect(() => {
    const el = railRef.current?.querySelector('.is-active');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const jump = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  return (
    <div className="page-container rn-page">
      <section className="page-header rn-header">
        <h1>Research</h1>
        <p className="page-subtitle">Theory and modelling of complex systems, from condensates to materials.</p>
      </section>

      <div className="rn-layout">
        {/* 左栏：转盘 + 目录，整体 sticky */}
        <aside className="rn-side">
          <ResearchReel />

          <nav className="rn-rail" ref={railRef} aria-label="Contents">
            <p className="rn-rail-head">Contents</p>
            <ul>
              {researchThemes.map(t => {
                const pubs = featuredPublications.filter(p => p.category === t.key);
                return (
                  <li key={t.key}>
                    <a
                      href={`#${t.key}`}
                      onClick={jump(t.key)}
                      className={`rn-rail-theme${activeId === t.key ? ' is-active' : ''}`}
                    >
                      {t.nav || t.label}
                    </a>
                    {/* 文章标题直接列出来，不点也知道里面有什么 */}
                    <ul className="rn-rail-subs">
                      {pubs.map(p => (
                        <li key={p.id}>
                          <a
                            href={`#${p.id}`}
                            onClick={jump(p.id)}
                            className={`rn-rail-sub${activeId === p.id ? ' is-active' : ''}`}
                            title={p.title}
                          >
                            {railLabel(p)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* 右栏：四个主题全部摊开，连续滚动 */}
        <main className="rn-main">
          {researchThemes.map(theme => {
            const pubs = featuredPublications.filter(p => p.category === theme.key);
            return (
              <section className="rn-section" key={theme.key} id={theme.key} data-spy>
                <header className="rn-section-head">
                  <h2>{theme.label}</h2>
                  {theme.blurb && <p>{theme.blurb}</p>}
                </header>
                {pubs.length === 0
                  ? <p className="rn-empty">(Nothing here yet)</p>
                  : pubs.map(p => <PaperCard key={p.id} pub={p} />)}
              </section>
            );
          })}
        </main>
      </div>
    </div>
  );
}

export default ResearchNew;
